/** Цели «отложить N к дате».
 *
 * Прогресс считается от общего накопленного нетто, а не от отдельного счёта:
 * физически деньги «под цель» никто не откладывает, и рисовать отдельный
 * кошелёк значило бы придумывать данные. Поэтому сверху — сколько накоплено
 * всего, и уже под ним цели, между которыми это накопленное распределяется.
 */
import { useEffect, useState } from "react";
import { ApiError, api, type Failure, type Goal, type Goals } from "../lib/api.ts";
import { formatMoney } from "../lib/money.ts";
import { percent, widthOf } from "../lib/percent.ts";
import { plural } from "../lib/plural.ts";
import { tap } from "../lib/telegram.ts";
import { useOnce } from "../lib/once.ts";
import { Header } from "../components/Header.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { Fallback } from "./Fallback.tsx";
import { AddButton, SheetRow, card, hint } from "../components/ui.tsx";

export function GoalsScreen({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<Goals | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [opened, setOpened] = useState<Goal | null>(null);

  const load = () => {
    setLoading(true);
    setFailure(null);
    api.goals().then(setData)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Цели" onBack={onBack}
              action={<AddButton label="Новая цель" onPick={() => setAdding(true)} />} />

      {loading || !data ? <Skeleton /> : (
        <>
          <section style={card}>
            <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>
              Накоплено всего
            </div>
            <div className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                          letterSpacing: "-0.8px", marginTop: 4 }}>
              {formatMoney(data.savings_pool, data.base_currency)}
            </div>
            <p style={hint}>
              Доходы минус расходы за всё время. Между целями оно распределяется мысленно —
              отдельных счетов под них нет.
            </p>
          </section>

          {data.items.length === 0 ? <Empty onAdd={() => setAdding(true)} /> : (
            <section style={{ ...card, marginTop: "var(--s-3)", padding: "4px var(--s-5)" }}>
              {data.items.map((goal, i) => (
                <GoalRow key={goal.id} goal={goal} base={data.base_currency}
                         last={i === data.items.length - 1} onOpen={() => setOpened(goal)} />
              ))}
            </section>
          )}
        </>
      )}

      {adding && <AddSheet onClose={() => setAdding(false)}
                           onDone={() => { setAdding(false); load(); }} />}
      {opened && <GoalSheet goal={opened} base={data?.base_currency ?? "USD"}
                            onClose={() => setOpened(null)}
                            onDone={() => { setOpened(null); load(); }} />}
    </div>
  );
}

function GoalRow({ goal, base, last, onOpen }: {
  goal: Goal; base: string; last: boolean; onOpen: () => void;
}) {
  const done = Number(goal.progress) >= 1;
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }}
      style={{ display: "block", width: "100%", padding: "14px 0", background: "none",
               border: "none", font: "inherit", color: "inherit", cursor: "pointer",
               textAlign: "left",
               borderBottom: last ? "none" : "1px solid var(--separator)" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ fontSize: "var(--f-body)", fontWeight: 500 }}>{goal.name}</span>
        <span className="num" style={{ fontSize: "var(--f-body)", fontWeight: 600 }}>
          {formatMoney(goal.saved_base, base)}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: "var(--surface-3)",
                    marginTop: 9, overflow: "hidden" }}>
        <div style={{ width: `${widthOf(goal.progress)}%`, height: "100%",
                      background: done ? "var(--income)" : "var(--data)" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7 }}>
        <span className="num" style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>
          {done
            ? "цель собрана"
            : goal.months_left === 0
              ? `осталось ${formatMoney(goal.required_monthly, goal.currency)}`
              : `${plural(goal.months_left, ["месяц", "месяца", "месяцев"])} по ` +
                `${formatMoney(goal.required_monthly, goal.currency)}`}
        </span>
        <span className="num" style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>
          {percent(goal.progress)} из {formatMoney(goal.target, goal.currency)}
        </span>
      </div>
    </button>
  );
}

function AddSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  const once = useOnce();

  const submit = () => {
    tap();
    setBusy(true);
    setTrouble("");
    once(() => api.addGoal(text.trim())
      .then((out) => (out.kind === "unclear" || out.kind === "failed"
        ? setTrouble(out.hint || "Не разобрал цель. Например: «отложить 3000 долларов к марту».")
        : onDone()))
      .catch(() => setTrouble("Не получилось сохранить. Попробуй ещё раз."))
      .finally(() => setBusy(false)));
  };

  return (
    <Sheet title="Новая цель" onClose={onClose}>
      <p style={{ ...hint, marginTop: 0, marginBottom: "var(--s-4)" }}>
        Сколько и к какому сроку. Дальше приложение само посчитает, сколько нужно в месяц.
      </p>
      <input value={text} onChange={(e) => setText(e.target.value)}
             placeholder="Отложить 3000 долларов к марту" aria-label="Цель"
             style={{ width: "100%", minHeight: "var(--tap)", background: "var(--surface-2)",
                      border: "none", borderRadius: "var(--r-field)", padding: "12px 14px",
                      color: "var(--text)", font: "inherit", fontSize: "var(--f-body)",
                       }} />
      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
      <button type="button" disabled={!text.trim() || busy} onClick={submit}
              style={{ ...primary, marginTop: "var(--s-4)",
                       background: text.trim() && !busy ? "var(--accent)" : "var(--surface-2)",
                       color: text.trim() && !busy ? "var(--on-accent)" : "var(--text-disabled)" }}>
        Добавить
      </button>
    </Sheet>
  );
}

function GoalSheet({ goal, base, onClose, onDone }: {
  goal: Goal; base: string; onClose: () => void; onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  return (
    <Sheet title={goal.name} onClose={onClose}>
      <div style={{ textAlign: "center", marginBottom: "var(--s-5)" }}>
        <div className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                      letterSpacing: "-0.8px" }}>
          {formatMoney(goal.saved_base, base)}
        </div>
        <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", marginTop: 4 }}>
          {percent(goal.progress)} из {formatMoney(goal.target, goal.currency)}
        </div>
      </div>
      <div style={{ background: "var(--surface)", borderRadius: "var(--r-card)",
                    padding: "2px var(--s-4)" }}>
        <SheetRow label="Срок" value={formatDay(goal.due_date)} />
        <SheetRow label="Осталось месяцев" value={String(goal.months_left)} />
        <SheetRow label="Нужно в месяц"
             value={formatMoney(goal.required_monthly, goal.currency)} last />
      </div>
      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
      <button type="button" aria-disabled={busy || undefined}
              onClick={() => {
                tap();
                setBusy(true);
                setTrouble("");
                api.deleteGoal(goal.id).then(onDone)
                  .catch(() => setTrouble("Не получилось удалить. Попробуй ещё раз."))
                  .finally(() => setBusy(false));
              }}
              style={{ ...primary, marginTop: "var(--s-4)", background: "transparent",
                       color: "var(--danger)",
                       border: "1px solid color-mix(in srgb, var(--danger) 30%, transparent)" }}>
        Убрать цель
      </button>
    </Sheet>
  );
}


function Empty({ onAdd }: { onAdd: () => void }) {
  return (
    <div style={{ ...card, marginTop: "var(--s-3)", textAlign: "center",
                  padding: "var(--s-8) var(--s-5)" }}>
      <p style={{ fontSize: "var(--f-body)", fontWeight: 600, margin: 0 }}>Целей пока нет</p>
      <p style={{ ...hint, marginTop: "var(--s-2)" }}>
        Цель превращает «надо бы накопить» в понятное «столько-то в месяц».
      </p>
      <button type="button" onClick={onAdd} style={{ ...primary, marginTop: "var(--s-4)" }}>
        Поставить цель
      </button>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={card} role="status" aria-busy="true" aria-label="Загружаю цели">
      <div style={{ height: 30, width: "40%", background: "var(--surface-3)", borderRadius: 7 }} />
    </div>
  );
}

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня",
                "июля", "августа", "сентября", "октября", "ноября", "декабря"];

function formatDay(iso: string): string {
  const [year, month, day] = iso.split("-");
  return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`;
}

const primary: React.CSSProperties = {
  width: "100%", minHeight: "var(--tap)", padding: "14px", borderRadius: "var(--r-field)",
  border: "none", cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
  fontWeight: 600, background: "var(--accent)", color: "var(--on-accent)",
};
